import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { useCurrentUser } from '@/features/profile/hooks/useCurrentUser'

interface PrivateRouteProps {
  children: React.ReactNode
  /** Redirect to /onboarding when the profile is incomplete (default: true) */
  requireCompleteProfile?: boolean
}

const PrivateRoute = ({ children, requireCompleteProfile = true }: PrivateRouteProps) => {
  const { isAuthenticated, checkAuth, logout } = useAuthStore()
  const { profileComplete, isPending, isError, refetch } = useCurrentUser()
  const [isChecking, setIsChecking] = useState(true)
  const location = useLocation()

  // Check and restore auth state on mount
  useEffect(() => {
    checkAuth()
    setIsChecking(false)
  }, [checkAuth])

  // Show nothing while checking auth (prevents flash redirect)
  if (isChecking) {
    return null
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (!requireCompleteProfile) {
    return <>{children}</>
  }

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <p className="text-gray-900 font-medium mb-1">We couldn't load your profile</p>
          <p className="text-gray-500 text-sm mb-4">Please check your connection and try again.</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => refetch()}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
            >
              Retry
            </button>
            <button
              onClick={logout}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50"
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-4 border-gray-200 border-t-blue-600 animate-spin" />
      </div>
    )
  }

  if (!profileComplete) {
    return (
      <Navigate
        to="/onboarding"
        state={{ redirectTo: `${location.pathname}${location.search}` }}
        replace
      />
    )
  }

  return <>{children}</>
}

export default PrivateRoute
